import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TagProgressBar from './TagProgressBar';

type TagProgressCardProps = {
    confirmedTags: number;
    totalTags: number;
    compact?: boolean;
};

const getProgressValue = (confirmedTags: number, totalTags: number) => {
    if (totalTags <= 0) {
        return 0;
    }

    const progress = Math.round((confirmedTags / totalTags) * 100);

    return Math.min(Math.max(progress, 0), 100);
};

const TagProgressCard = ({ confirmedTags, totalTags, compact = false }: TagProgressCardProps) => {
    const { t } = useTranslation('common');

    const progress = useMemo(() => {
        return getProgressValue(confirmedTags, totalTags);
    }, [confirmedTags, totalTags]);

    if (compact) {
        return (
            <Box
                sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    border: '2px solid #D6E4FF',
                    bgcolor: '#FFFFFF',
                    mb: 0.5,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        mb: 0.75,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: '#0F172A',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {t('Tag Registration Progress')}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: progress >= 100 ? '#15803D' : '#2563EB',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {progress}% · {confirmedTags}/{totalTags}
                    </Typography>
                </Box>

                <TagProgressBar
                    confirmedTags={confirmedTags}
                    totalTags={totalTags}
                    sx={{
                        '& .MuiLinearProgress-root': {
                            height: 6,
                        },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 1px 8px rgba(0, 0, 0, 0.04)',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        {t('Tag Registration Progress')}
                    </Typography>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: 'block',
                            mt: 0.25,
                        }}
                    >
                        {t('Confirming tags. This may take a few minutes.')}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={700}>
                        {progress} %
                    </Typography>
                </Box>
            </Box>

            <TagProgressBar confirmedTags={confirmedTags} totalTags={totalTags} />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 1,
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {t('Confirmed')}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                    {confirmedTags} / {totalTags}
                </Typography>
            </Box>
        </Box>
    );
};

export default TagProgressCard;
